import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { BQLink } from '../../../../atoms/bqlink';
import Loader from '../../../../atoms/icons/loader';
import { ArrowRightIcon } from '../../../../atoms/material-icons';
import EmptyState from '../empty-state';
import './put-call-ratio-options.m.css';
import { isWithinTradingHours, widgetLink } from '../../../../../helpers/utils';
import { useSelector } from 'react-redux';
import get from 'lodash/get';

const PutCallRatioOptions = ({ widget = false }) => {
  const widgetParam = widget ? '?view=apps' : '';
  const [putCallRatioData, setPutCallRatioData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('stock');
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );

  const getPutCallRatioDataForQuery = async (type = 'stock') => {
    const response = await fetch(`/foapi/option/getPcr?instrument=${type === 'stock' ? '2' : '1'}`);
    return response.json();
  };

  const resultPutCallRatioData = useQuery(
    'getPutCallRatioData',
    () => getPutCallRatioDataForQuery(selectedFilter),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setPutCallRatioData(data?.data);
      },
      enabled:
        initialLoading ||
        (!!selectedFilter && isWithinTradingHours(tradingHolidays, specialTradingDays)),
    },
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (resultPutCallRatioData?.isSuccess) {
      setPutCallRatioData(resultPutCallRatioData?.data?.data);
    }
  }, []);

  const clickHandler = async (type) => {
    setLoading(true);
    setSelectedFilter(type);
    const data = await getPutCallRatioDataForQuery(type);
    setPutCallRatioData(data?.data);
    setLoading(false);
  };

  return (
    <React.Fragment>
      {initialLoading ? (
        <div styleName="initial-loading-container"></div>
      ) : (
        <div styleName={`open-interest-breakup-container ${widget ? 'widget-mode' : ''}`}>
          <div styleName="header-strip">
            <span styleName="title">PUT CALL RATIO</span>
            <div styleName="filter-btn-container">
              <button
                styleName={`filter-btn ${selectedFilter === 'stock' ? 'filter-selected' : ''}`}
                onClick={() => clickHandler('stock')}
              >
                Stock Options
              </button>
              <button
                styleName={`filter-btn ${selectedFilter === 'index' ? 'filter-selected' : ''}`}
                onClick={() => clickHandler('index')}
              >
                Index Options
              </button>
            </div>
          </div>
          {putCallRatioData && putCallRatioData?.length !== 0 ? (
            <>
              {loading ? (
                <div styleName="loading-container-ext">
                  <div styleName="loading-container-int">
                    <Loader color={'#dadee1'} />
                  </div>
                </div>
              ) : (
                <div styleName="content-container">
                  <div styleName="table-body-container">
                    <table className="desktop-only">
                      <thead>
                        <tr styleName="initial-header-container">
                          <th colSpan={1}></th>
                          <th colSpan={1}></th>
                          <th colSpan={4}>Open Interest</th>
                          <th colSpan={3}>PCR OI</th>
                          <th colSpan={3}>PCR Vol</th>
                        </tr>
                        <tr styleName="second-header-container">
                          <th styleName="column-title">Security</th>
                          <th styleName="column-title-alt ">
                            Spot
                            <br />
                            Price
                          </th>
                          <th styleName="column-title-alt highlighted-container">Call</th>
                          <th styleName="column-title-alt highlighted-container">Change</th>
                          <th styleName="column-title-alt highlighted-container">PUT</th>
                          <th styleName="column-title-alt highlighted-container">Change</th>
                          <th styleName="column-title-alt ">Current</th>
                          <th styleName="column-title-alt ">Previous</th>
                          <th styleName="column-title-alt ">Change</th>
                          <th styleName="column-title-alt highlighted-container">Current</th>
                          <th styleName="column-title-alt highlighted-container">Previous</th>
                          <th styleName="column-title-alt highlighted-container">Change</th>
                        </tr>
                        <tr styleName="initial-header-container">
                          <th colSpan={1}></th>
                          <th colSpan={1}></th>
                          <th colSpan={4}>Open Interest</th>
                          <th colSpan={3}>PCR OI</th>
                          <th colSpan={3}>PCR Vol</th>
                        </tr>
                        <tr styleName="second-header-container">
                          <th styleName="column-title">Security</th>
                          <th styleName="column-title-alt ">
                            Spot
                            <br />
                            Price
                          </th>
                          <th styleName="column-title-alt highlighted-container">Call</th>
                          <th styleName="column-title-alt highlighted-container">Change</th>
                          <th styleName="column-title-alt highlighted-container">PUT</th>
                          <th styleName="column-title-alt highlighted-container">Change</th>
                          <th styleName="column-title-alt ">Current</th>
                          <th styleName="column-title-alt ">Previous</th>
                          <th styleName="column-title-alt ">Change</th>
                          <th styleName="column-title-alt highlighted-container">Current</th>
                          <th styleName="column-title-alt highlighted-container">Previous</th>
                          <th styleName="column-title-alt highlighted-container">Change</th>
                        </tr>
                      </thead>
                      <tbody styleName="table-body">
                        {Array.isArray(putCallRatioData) &&
                          putCallRatioData?.slice(0, 8)?.map((dataPoint, index) => (
                            <tr styleName="data-row" key={index}>
                              <td>
                                {dataPoint?.['symbol'] ? (
                                  <span styleName="name">{dataPoint?.['symbol']}</span>
                                ) : (
                                  <span styleName="name">-</span>
                                )}
                              </td>
                              <td styleName="column-value ">
                                {dataPoint['spot-price'] ? (
                                  <span>{dataPoint?.['spot-price']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value highlighted-container">
                                {dataPoint['call-open-interest'] ? (
                                  <span>{dataPoint?.['call-open-interest']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value highlighted-container">
                                {dataPoint['call-open-interest-change'] ? (
                                  <span>
                                    {String(dataPoint['call-open-interest-change']).includes(
                                      '-',
                                    ) ? (
                                      <span styleName="negative-value">
                                        {dataPoint['call-open-interest-change']}
                                      </span>
                                    ) : (
                                      <span styleName="positive-value">
                                        {`+${dataPoint['call-open-interest-change']}`}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value highlighted-container">
                                {dataPoint['put-open-interest'] ? (
                                  <span>{dataPoint?.['put-open-interest']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value highlighted-container">
                                {dataPoint['put-open-interest-change'] ? (
                                  <span>
                                    {String(dataPoint['put-open-interest-change']).includes('-') ? (
                                      <span styleName="negative-value">
                                        {dataPoint['put-open-interest-change']}
                                      </span>
                                    ) : (
                                      <span styleName="positive-value">
                                        {`+${dataPoint['put-open-interest-change']}`}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value ">
                                {dataPoint['pcr-open-interest-current'] ? (
                                  <span>
                                    {dataPoint?.['pcr-open-interest-current']?.toFixed(2)}
                                  </span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value ">
                                {dataPoint['pcr-open-interest-previous'] ? (
                                  <span>
                                    {String(dataPoint['pcr-open-interest-previous']).includes(
                                      '-',
                                    ) ? (
                                      <span styleName="negative-value">
                                        {dataPoint['pcr-open-interest-previous']?.toFixed(2)}
                                      </span>
                                    ) : (
                                      <span styleName="positive-value">
                                        {`+${dataPoint['pcr-open-interest-previous']?.toFixed(2)}`}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value ">
                                {dataPoint['pcr-open-interest-change'] ? (
                                  <span>
                                    {String(dataPoint['pcr-open-interest-change']).includes('-') ? (
                                      <span styleName="negative-value">
                                        {dataPoint['pcr-open-interest-change']?.toFixed(2)}
                                      </span>
                                    ) : (
                                      <span styleName="positive-value">
                                        {`+${dataPoint['pcr-open-interest-change']?.toFixed(2)}`}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value highlighted-container">
                                {dataPoint['pcr-volume-current'] ? (
                                  <span>{dataPoint?.['pcr-volume-current']?.toFixed(2)}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value highlighted-container">
                                {dataPoint['pcr-volume-previous'] ? (
                                  <span>
                                    {String(dataPoint['pcr-volume-previous']).includes('-') ? (
                                      <span styleName="negative-value">
                                        {dataPoint['pcr-volume-previous']?.toFixed(2)}
                                      </span>
                                    ) : (
                                      <span styleName="positive-value">
                                        {`+${dataPoint['pcr-volume-previous']?.toFixed(2)}`}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value highlighted-container">
                                {dataPoint['pcr-volume-change'] ? (
                                  <span>
                                    {String(dataPoint['pcr-volume-change']).includes('-') ? (
                                      <span styleName="negative-value">
                                        {dataPoint['pcr-volume-change']?.toFixed(2)}
                                      </span>
                                    ) : (
                                      <span styleName="positive-value">
                                        {`+${dataPoint['pcr-volume-change']?.toFixed(2)}`}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    <table className="mobile-only">
                      <thead styleName="table-header-mobile">
                        <tr>
                          <th styleName="column-title">Security</th>
                          <th styleName="column-title-alt">
                            Call
                            <br /> OI
                          </th>
                          <th styleName="column-title-alt">
                            Put
                            <br /> OI
                          </th>
                          <th styleName="column-title-alt">
                            PCR
                            <br />
                            OI
                          </th>
                          <th styleName="column-title-alt">
                            PCR <br />
                            Vol
                          </th>
                        </tr>
                        <tr>
                          <th styleName="column-title">Security</th>
                          <th styleName="column-title-alt">
                            Call
                            <br /> OI
                          </th>
                          <th styleName="column-title-alt">
                            Put
                            <br /> OI
                          </th>
                          <th styleName="column-title-alt">
                            PCR
                            <br />
                            OI
                          </th>
                          <th styleName="column-title-alt">
                            PCR <br />
                            Vol
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(putCallRatioData) &&
                          putCallRatioData?.slice(0, 8)?.map((dataPoint, index) => (
                            <tr styleName="data-row-alt" key={index}>
                              <td>
                                {dataPoint?.['symbol'] ? (
                                  <span styleName="name">{dataPoint?.['symbol']}</span>
                                ) : (
                                  <span styleName="name">-</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {dataPoint?.['call-open-interest'] ? (
                                  <span>{dataPoint?.['call-open-interest']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {dataPoint?.['put-open-interest'] ? (
                                  <span>{dataPoint?.['put-open-interest']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {dataPoint?.['pcr-open-interest-current'] ? (
                                  <span>
                                    {dataPoint?.['pcr-open-interest-current']?.toFixed(2)}
                                  </span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {dataPoint?.['pcr-volume-current'] ? (
                                  <span>{dataPoint?.['pcr-volume-current']?.toFixed(2)}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div styleName="no-data-container-ext">
              <div styleName="no-data-container-int">
                <EmptyState />
              </div>
            </div>
          )}

          {!widget && (
            <div styleName="view-btn-container">
              <BQLink
                styleName="view-more-btn"
                href={widgetLink(`/markets/option-chain/put-call-ratio${widgetParam}`, widget)}
                target="_blank"
                rel="noreferrer"
              >
                View All Options
                <ArrowRightIcon styleName="tab-icon" color={'#2b2b2b'} />
              </BQLink>
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default PutCallRatioOptions;
